import {
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  FoodicsInventoryItemEntity,
  FoodicsUnitSource,
} from "src/types/foodics";

/**
 * Foodics tracks two units per item (ingredient + storage); Jazr stores one.
 * This hook holds the per-item choice with a single default so newly-paged rows
 * inherit the "set all" selection, and per-row overrides stay stable while paging.
 */
export const useUnitSourceChoice = (
  initial: FoodicsUnitSource = "ingredient"
) => {
  const [defaultUnitSource, setDefaultUnitSource] =
    useState<FoodicsUnitSource>(initial);
  const [overrides, setOverrides] = useState<
    Record<string, FoodicsUnitSource>
  >({});

  const getUnitSource = useCallback(
    (id: string): FoodicsUnitSource => overrides[id] ?? defaultUnitSource,
    [overrides, defaultUnitSource]
  );

  const setRowUnitSource = useCallback(
    (id: string, source: FoodicsUnitSource) =>
      setOverrides((prev) => ({ ...prev, [id]: source })),
    []
  );

  // "Set all" resets the shared default and clears per-row overrides so every
  // row — including ones not yet loaded — follows the new choice.
  const setAllUnitSource = useCallback((source: FoodicsUnitSource) => {
    setDefaultUnitSource(source);
    setOverrides({});
  }, []);

  return {
    defaultUnitSource,
    getUnitSource,
    setRowUnitSource,
    setAllUnitSource,
  };
};

type UnitSourceSetAllProps = {
  value: FoodicsUnitSource;
  onChange: (source: FoodicsUnitSource) => void;
};

/** Header control that applies one unit choice to every item at once. */
export const UnitSourceSetAll = ({
  value,
  onChange,
}: UnitSourceSetAllProps) => {
  const { t } = useTranslation("integrations");

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      <Typography fontSize={12} color="#667085">
        {t("menuImport.unitChoice.setAll")}
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={value}
        onChange={(_, next) => {
          if (next) onChange(next as FoodicsUnitSource);
        }}
      >
        <ToggleButton value="ingredient" sx={{ textTransform: "none", py: 0.25 }}>
          {t("menuImport.unitChoice.ingredient")}
        </ToggleButton>
        <ToggleButton value="storage" sx={{ textTransform: "none", py: 0.25 }}>
          {t("menuImport.unitChoice.storage")}
        </ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  );
};

type UnitSourceSelectProps = {
  row: FoodicsInventoryItemEntity;
  value: FoodicsUnitSource;
  onChange: (source: FoodicsUnitSource) => void;
  disabled?: boolean;
};

/** Per-row dropdown choosing which Foodics unit the imported item adopts. */
export const UnitSourceSelect = ({
  row,
  value,
  onChange,
  disabled,
}: UnitSourceSelectProps) => {
  const { t } = useTranslation("integrations");
  const noUnit = t("menuImport.unitChoice.noUnit");
  const ingredientLabel =
    typeof row.ingredient_unit === "string" && row.ingredient_unit.trim()
      ? row.ingredient_unit
      : noUnit;
  const storageLabel =
    typeof row.storage_unit === "string" && row.storage_unit.trim()
      ? row.storage_unit
      : noUnit;

  return (
    <Select
      size="small"
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value as FoodicsUnitSource)}
      sx={{ minWidth: 190, fontSize: 13 }}
    >
      <MenuItem value="ingredient" sx={{ fontSize: 13 }}>
        {t("menuImport.unitChoice.ingredient")} ({ingredientLabel})
      </MenuItem>
      <MenuItem value="storage" sx={{ fontSize: 13 }}>
        {t("menuImport.unitChoice.storage")} ({storageLabel})
      </MenuItem>
    </Select>
  );
};
